import { defineComponent as _defineComponent } from 'vue'
import { vShow as _vShow, createVNode as _createVNode, withDirectives as _withDirectives, unref as _unref, resolveDynamicComponent as _resolveDynamicComponent, normalizeClass as _normalizeClass, openBlock as _openBlock, createBlock as _createBlock, createElementVNode as _createElementVNode, Fragment as _Fragment, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = { class: "flex gap-x-2.5" }
const _hoisted_2 = { class: "flex gap-x-4" }
const _hoisted_3 = /*#__PURE__*/_createElementVNode("span", null, "Due date", -1)
const _hoisted_4 = { class: "flex gap-x-4" }
const _hoisted_5 = /*#__PURE__*/_createElementVNode("span", null, "Set reminder", -1)
const _hoisted_6 = { class: "flex gap-x-4 pt-3" }

import { computed, ref, reactive, watch, onMounted, nextTick, watchEffect, onUpdated } from "vue"
import GgmsInput from "@/components/GgmsInput.vue"
import GgmsTextarea from "@/components/GgmsTextarea.vue"
import GgmsCheckBox from "@/components/GgmsCheckBox.vue"
import GgmsDropdown from "@/components/GgmsDropdown.vue"
import GgmsMultiSelect from "@/components/GgmsMultiSelect.vue"
import { CalendarIcon, ClockIcon, CheckCircleIcon as CheckCircleIconOutline } from "@heroicons/vue/outline"
import { BellIcon, CheckCircleIcon } from "@heroicons/vue/solid"
import { useTaskStore } from "@/stores/task"
import { useAgentsStore } from "@/stores/agents"
import { useGridCollectionStore } from "@/stores/grid-collection"
import { useAuthStore } from "@/stores/auth"
import { useAgencyStore } from "@/stores/agency"
import { useWorkflowStore } from "@/stores/workflow"
import { useTagStore } from "@/stores/tag"
import {
    isInvalid,
    hasErrorMessage,
    getDateInputFormat,
    getTimeInputFormat,
    isAdmin,
    getDomainId,
    getTaskTypes,
    getTaskReminderOptions,
    formatDateToIso,
} from "@/shared/utils/helpers"
import { Task } from "@/shared/models/task"
import { Tag } from "@/shared/models/tag"
import GgmsLoading from "../GgmsLoading.vue"

interface Props {
    modalAction?: string
    taskId?: string
    leadId?: string
    isNotEditableByUser?: boolean
    isCreateTaskModalOpen?: boolean
    isForWorkflow?: boolean
    taskValue?: Task
    errorValue?: { field: string; message: string }
}


export default /*#__PURE__*/_defineComponent({
  __name: 'CreateTaskForm',
  props: {
    modalAction: {},
    taskId: {},
    leadId: {},
    isNotEditableByUser: { type: Boolean },
    isCreateTaskModalOpen: { type: Boolean },
    isForWorkflow: { type: Boolean },
    taskValue: {},
    errorValue: {}
  },
  emits: ["update:task"],
  setup(__props: any, { emit: __emit }) {

const taskStore = useTaskStore()
const agentStore = useAgentsStore()
const authStore = useAuthStore()
const agencyStore = useAgencyStore()
const gridCollectionStore = useGridCollectionStore()
const workflowStore = useWorkflowStore()
const tagStore = useTagStore()

const props = __props
const emit = __emit

const form = ref()
const error = ref()
const isTaskLoading = ref(true)
const queueDate = ref("")
const queueTime = ref("")
const firstInput = ref()
const isFirstLoad = ref(true)

const tags = ref<Tag[]>([])
const tag = ref<Tag[]>([])

const taskTypes = ref(getTaskTypes())
const reminderOptions = ref(getTaskReminderOptions())

const cachedAgents = computed(() => agentStore.tableState.data)
const currentUser = computed(() => authStore.currentUser)
const collections = computed(() => gridCollectionStore.collectionTableState.data)

const task: Task = reactive({
    _id: "",
    displayName: "",
    description: "",
    when: {
        startAt: "",
        timezone: "America/Los_Angeles",
    },
    reminder: reminderOptions.value[0].attribute,
    shouldRemoveIfNotCompleted: true,
    agent: {
        _id: currentUser?.value?._id,
        fullName: currentUser?.value?.fullName,
    },
    agentId: currentUser?.value?._id,
    collection: {
        _id: "",
        displayName: "",
    },
    type: "",
    collectionId: "",
    status: "",
    notes: "",
    domainId: "",
    unitsOfTimeToRemove: "30",
    typeOfTimeToRemove: "days",
})

const domainOptions = computed(() =>
    agencyStore.agency.agencyDomains.map((domain) => ({ url: domain.url, _id: domain._id }))
)
const notCompletedOptions = [
    { value: "days", attribute: "days" },
    { value: "hours", attribute: "hours" },
    { value: "minutes", attribute: "minutes" },
]
const selectedDomain = ref(initializeDomain())

const isTaskCompleted = computed(() => task.status === "completed")
const getCheckCircleIconType = computed(() => (isTaskCompleted.value ? CheckCircleIcon : CheckCircleIconOutline))
const getStatusIconColor = computed(() =>
    isTaskCompleted.value
        ? "fill-primary-color-600 text-white hover:fill-primary-color-700"
        : "text-gray-400 hover:text-gray-500"
)
const getStatusCursorType = computed(() => {
    if (props.isNotEditableByUser) {
        return "cursor-not-allowed"
    }
    if (props.modalAction === "edit") {
        return "cursor-pointer"
    }
    return ""
})

function setQueueDateAndTime(date: string) {
    const isoDateInAgencyTimezone = formatDateToIso(new Date(date))
    const [datePart, timePart] = isoDateInAgencyTimezone.split("T")
    queueDate.value = datePart
    queueTime.value = timePart.replace("Z", "")
}

function setTaskStartDate() {
    try {
        const date = new Date(queueDate.value)
        const hours = Number(queueTime.value.split(":")[0])
        const minutes = Number(queueTime.value.split(":")[1])

        if (task.when === undefined) {
            task.when = {
                startAt: formatDateToIso(new Date(date.getFullYear(), date.getMonth(), date.getDate(), hours, minutes)),
                timezone: "America/Los_Angeles",
            }
        } else {
            // Manually set the date and time as we don't want other conversions
            const year = date.getFullYear()
            const month = String(date.getMonth() + 1).padStart(2, "0")
            const day = String(date.getDate()).padStart(2, "0")
            const hour = String(hours).padStart(2, "0")
            const minute = String(minutes).padStart(2, "0")
            task.when.startAt = `${year}-${month}-${day}T${hour}:${minute}:00.000Z`
        }
    } catch (error) {
        return
    }
}

async function changeStatusOfTask() {
    if (props.isNotEditableByUser) {
        return
    }

    if (props.taskId) {
        if (props.modalAction === "edit") {
            const status = isTaskCompleted.value ? "pending" : "completed"
            await taskStore.updateTaskStatus(props.taskId, status)
            task.status = status
            taskStore.getTasks()
        }
    }
}

function initializeDomain() {
    const domain = getDomainId()
    task.domainId = domain
    return domain === "all" ? null : domain
}

function scrollToErrorField() {
    if (props.errorValue && !props.errorValue.field) {
        return
    }
    const errorField = form.value.querySelector(`[name="${error.value.field}"]`) || form.value.querySelector("input")
    if (errorField) {
        errorField.scrollIntoView({ behavior: "smooth" })
    }
}

onUpdated(() => {
    nextTick(() => {
        const inputElement = firstInput.value?.$el.querySelector("input")
        if (inputElement && isFirstLoad.value) {
            inputElement.focus()
            isFirstLoad.value = false
        }
    })
})

onMounted(async () => {
    gridCollectionStore.collectionType = "task"
    if (isAdmin() && !cachedAgents.value.length) {
        await agentStore.loadAgents()
    }
    //get tags for task
    tags.value = await tagStore.getTagsForTasks()

    if (props.isForWorkflow) {
        setQueueDateAndTime(new Date())
        if (workflowStore.modelValue) {
            Object.assign(task, workflowStore.modelValue)
            if (workflowStore.modelValue.domainId) {
                task.domainId = workflowStore.modelValue.domainId
            } else {
                initializeDomain()
            }
            if (task.when) {
                setQueueDateAndTime(new Date(task.when.startAt))
            }
            if (workflowStore.modelValue.tags) tag.value = workflowStore.modelValue.tags
            task.unitsOfTimeToRemove = workflowStore.modelValue.unitsOfTimeToRemove || "30"
            task.typeOfTimeToRemove = workflowStore.modelValue.typeOfTimeToRemove || "days"
        }
        isTaskLoading.value = false
    } else if (props.taskId) {
        try {
            isTaskLoading.value = true
            const response = await taskStore.getTask(props.taskId)
            task.displayName = response.task.displayName
            task.description = response.task.description
            task.when = {
                startAt: response.task.when?.startAt,
                timezone: response.task.when?.timezone || "America/Los_Angeles",
            }
            task.reminder =
                reminderOptions.value.find((option) => option.attribute === response.task.reminder)?.attribute ||
                "minutes_30"
            task.shouldRemoveIfNotCompleted = response.task.shouldRemoveIfNotCompleted || false
            task.agent = {
                _id: response.task.agent?._id,
                fullName: response.task.agent?.fullName,
            }
            if (response.task.agent._id) task.agentId = response.task.agent?._id
            task.type = taskTypes.value.find((option) => option.attribute === response.task.type)?.attribute || ""
            task.collection = {
                _id: response.task.collection?._id,
                displayName: response.task.collection?.displayName,
            }
            task.collectionId = response.task.collection?._id
            task.status = response.task.status
            task.notes = response.task.notes
            task.domainId = response.task.domainId || ""
            selectedDomain.value = domainOptions.value.find((option) => option._id === task?.domainId)?._id
            if (task.when) {
                setQueueDateAndTime(task.when.startAt)
            }
            if (response.task.tags) tag.value = response.task.tags
            task.unitsOfTimeToRemove = response.task.unitsOfTimeToRemove || "30"
            task.typeOfTimeToRemove = response.task.typeOfTimeToRemove || "days"
            isTaskLoading.value = false
        } catch (e) {
            console.log(`There was an error getting task with id: ${props.taskId}`, e)
        } finally {
            isTaskLoading.value = false
        }
    } else {
        isTaskLoading.value = false
    }
})

watch(
    () => agencyStore.selectedDomain,
    (newValue) => {
        selectedDomain.value = newValue._id === "all" ? "" : newValue._id
    }
)

watch(
    () => queueDate.value,
    () => {
        setTaskStartDate()
    }
)

watch(
    () => queueTime.value,
    () => {
        setTaskStartDate()
    }
)

watch(
    () => selectedDomain,
    () => {
        task.domainId = selectedDomain.value
    }
)

watch(
    () => task,
    () => {
        setTaskStartDate()
        emit("update:task", task)
    },
    { deep: true }
)

watch(
    () => tag,
    () => {
        task.tags = tag.value
    },
    { deep: true }
)

watch(
    () => props.errorValue,
    () => {
        if (!props.errorValue) {
            error.value = {
                field: "",
                message: "",
            }
            return
        }
        error.value = props.errorValue
        scrollToErrorField()
    }
)

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _withDirectives(_createVNode(GgmsLoading, null, null, 512), [
      [_vShow, isTaskLoading.value]
    ]),
    _withDirectives(_createElementVNode("div", {
      ref_key: "form",
      ref: form,
      class: "flex flex-col gap-y-[30px]"
    }, [
      _createElementVNode("div", _hoisted_1, [
        _createVNode(GgmsInput, {
          modelValue: task.displayName,
          "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((task.displayName) = $event)),
          ref_key: "firstInput",
          ref: firstInput,
          class: "grow",
          name: "task-name",
          label: "Task Name",
          invalid: _unref(isInvalid)('displayname', error.value),
          errorMessage: _unref(hasErrorMessage)('displayname', error.value)
        }, null, 8, ["modelValue", "invalid", "errorMessage"]),
        (_openBlock(), _createBlock(_resolveDynamicComponent(getCheckCircleIconType.value), {
          class: _normalizeClass(["h-9 w-9 mt-7 cursor-pointer", [getStatusIconColor.value, getStatusCursorType.value]]),
          onClick: changeStatusOfTask
        }, null, 8, ["class"]))
      ]),
      _createElementVNode("div", _hoisted_2, [
        _createVNode(GgmsDropdown, {
          modelValue: task.type,
          "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((task.type) = $event)),
          options: taskTypes.value,
          name: "task-type",
          label: "Task Type",
          optionLabel: "value",
          optionValue: "attribute",
          placeholder: "Select task type",
          invalid: _unref(isInvalid)('type', error.value),
          errorMessage: _unref(hasErrorMessage)('type', error.value),
          class: "w-full"
        }, null, 8, ["modelValue", "options", "invalid", "errorMessage"]),
        _createVNode(GgmsDropdown, {
          modelValue: task.collectionId,
          "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((task.collectionId) = $event)),
          options: collections.value,
          name: "task-queue",
          label: "Task List",
          optionLabel: "displayName",
          optionValue: "_id",
          placeholder: "Select task list",
          class: "w-full"
        }, null, 8, ["modelValue", "options"])
      ]),
      _createVNode(GgmsDropdown, {
        modelValue: task.agentId,
        "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((task.agentId) = $event)),
        options: cachedAgents.value,
        name: "agents",
        label: "Assigned to",
        optionLabel: "fullName",
        optionValue: "_id",
        placeholder: "Select agent",
        invalid: _unref(isInvalid)('agentid', error.value),
        errorMessage: _unref(hasErrorMessage)('agentid', error.value),
        class: "w-full"
      }, null, 8, ["modelValue", "options", "invalid", "errorMessage"]),
      _createElementVNode("div", null, [
        _createElementVNode("div", {
          class: _normalizeClass(["flex gap-x-4", _ctx.isForWorkflow ? 'flex-col gap-y-4' : 'flex-row'])
        }, [
          _createElementVNode("div", null, [
            _hoisted_3,
            _createElementVNode("div", _hoisted_4, [
              _createVNode(GgmsInput, {
                modelValue: queueDate.value,
                "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((queueDate).value = $event)),
                type: "date",
                iconStart: _unref(CalendarIcon),
                invalid: _unref(isInvalid)('queueDate', error.value),
                errorMessage: _unref(hasErrorMessage)('queueDate', error.value),
                class: "w-[150px]"
              }, null, 8, ["modelValue", "iconStart", "invalid", "errorMessage"]),
              _createVNode(GgmsInput, {
                modelValue: queueTime.value,
                "onUpdate:modelValue": _cache[5] || (_cache[5] = ($event: any) => ((queueTime).value = $event)),
                type: "time",
                iconStart: _unref(ClockIcon),
                invalid: _unref(isInvalid)('queueTime', error.value),
                errorMessage: _unref(hasErrorMessage)('queueTime', error.value),
                class: "w-36"
              }, null, 8, ["modelValue", "iconStart", "invalid", "errorMessage"])
            ])
          ]),
          _createElementVNode("div", null, [
            _hoisted_5,
            _createVNode(GgmsDropdown, {
              modelValue: task.reminder,
              "onUpdate:modelValue": _cache[6] || (_cache[6] = ($event: any) => ((task.reminder) = $event)),
              options: reminderOptions.value,
              name: "reminder",
              optionLabel: "value",
              optionValue: "attribute",
              placeholder: "Select reminder",
              class: "w-full"
            }, null, 8, ["modelValue", "options"])
          ])
        ], 2)
      ]),
      _createElementVNode("div", null, [
        _createVNode(GgmsCheckBox, {
          modelValue: task.shouldRemoveIfNotCompleted,
          "onUpdate:modelValue": _cache[7] || (_cache[7] = ($event: any) => ((task.shouldRemoveIfNotCompleted) = $event)),
          name: "remove-if-not-completed",
          label: "Remove task if not completed in (after Due Date)",
          isLabelClickable: true
        }, null, 8, ["modelValue"]),
        _createElementVNode("div", _hoisted_6, [
          _createVNode(GgmsInput, {
            modelValue: task.unitsOfTimeToRemove,
            "onUpdate:modelValue": _cache[8] || (_cache[8] = ($event: any) => ((task.unitsOfTimeToRemove) = $event)),
            disabled: !task.shouldRemoveIfNotCompleted
          }, null, 8, ["modelValue", "disabled"]),
          _createVNode(GgmsDropdown, {
            modelValue: task.typeOfTimeToRemove,
            "onUpdate:modelValue": _cache[9] || (_cache[9] = ($event: any) => ((task.typeOfTimeToRemove) = $event)),
            options: notCompletedOptions,
            disabled: !task.shouldRemoveIfNotCompleted,
            optionLabel: "value",
            optionValue: "attribute",
            placeholder: "Select time",
            class: "w-full"
          }, null, 8, ["modelValue", "disabled"])
        ])
      ]),
      _createVNode(GgmsMultiSelect, {
        modelValue: tag.value,
        "onUpdate:modelValue": _cache[10] || (_cache[10] = ($event: any) => ((tag).value = $event)),
        label: "Add Tags",
        placeholder: "+ Add Tag",
        display: "chip",
        options: tags.value,
        optionLabel: "displayName",
        invalid: _unref(isInvalid)('tags', error.value),
        errorMessage: _unref(hasErrorMessage)('tags', error.value),
        classes: "w-full"
      }, null, 8, ["modelValue", "options", "invalid", "errorMessage"]),
      _createVNode(GgmsTextarea, {
        modelValue: task.description,
        "onUpdate:modelValue": _cache[11] || (_cache[11] = ($event: any) => ((task.description) = $event)),
        placeholder: "Add description",
        label: "Task description",
        rows: 5,
        invalid: _unref(isInvalid)('description', error.value),
        errorMessage: _unref(hasErrorMessage)('description', error.value)
      }, null, 8, ["modelValue", "invalid", "errorMessage"]),
      _createVNode(GgmsTextarea, {
        modelValue: task.notes,
        "onUpdate:modelValue": _cache[12] || (_cache[12] = ($event: any) => ((task.notes) = $event)),
        placeholder: "Add notes",
        label: "Notes",
        rows: 5
      }, null, 8, ["modelValue"]),
      _createVNode(GgmsDropdown, {
        modelValue: selectedDomain.value,
        "onUpdate:modelValue": _cache[13] || (_cache[13] = ($event: any) => ((selectedDomain).value = $event)),
        options: domainOptions.value,
        label: "Domain",
        name: "domain",
        isShowClear: true,
        optionLabel: "url",
        optionValue: "_id",
        placeholder: "Select Domain",
        class: "w-full",
        invalid: _unref(isInvalid)('domain', error.value),
        "error-message": _unref(hasErrorMessage)('domain', error.value)
      }, null, 8, ["modelValue", "options", "invalid", "error-message"])
    ], 512), [
      [_vShow, !isTaskLoading.value]
    ])
  ], 64))
}
}

})